import { IconaCartella } from '../../componenti/Icone.tsx'
import type { AmbitoRicerca, CartellaTrovata } from '../../servizi/posta.ts'
import '../../stile-posta-ricerca.css'

/**
 * La ricerca nella posta come in Roundcube (decisione di Davide del 3/11/2026, dopo la segnalazione di Carmem
 * «il campo RICERCA non funziona, non cerca e non trova»): accanto al campo «Cerca» la scelta di dove cercare,
 * e su ogni risultato la cartella (e la casella, se la ricerca ne guarda piu' d'una). Le regole stanno
 * nell'API: apps/api/src/servizi/posta-ricerca-ambito.ts.
 */

export const NOMI_AMBITO: Record<AmbitoRicerca, string> = {
  cartella: 'Questa cartella',
  casella: 'Tutte le cartelle',
  caselle: 'Tutte le caselle',
}

/**
 * Dove si cerca davvero: «Tutte le caselle» non c'e' in supervisione (la supervisione riguarda una casella)
 * ne' con una casella sola, e vale come «Tutte le cartelle».
 */
export function ambitoEffettivo(scelto: AmbitoRicerca, conCaselle: boolean): AmbitoRicerca {
  return scelto === 'caselle' && !conCaselle ? 'casella' : scelto
}

/** La tendina accanto al campo «Cerca»: la scelta resta fra le preferenze di chi la fa. */
export function SceltaAmbito({ valore, conCaselle, cambia }: {
  valore: AmbitoRicerca
  /** «Tutte le caselle» fra le scelte. */
  conCaselle: boolean
  cambia: (a: AmbitoRicerca) => void
}) {
  return (
    <select className="posta-ricerca-ambito" value={ambitoEffettivo(valore, conCaselle)} aria-label="Dove cercare"
            title="Dove cercare" onChange={(e) => cambia(e.target.value as AmbitoRicerca)}>
      <option value="cartella">{NOMI_AMBITO.cartella}</option>
      <option value="casella">{NOMI_AMBITO.casella}</option>
      {conCaselle && <option value="caselle">{NOMI_AMBITO.caselle}</option>}
    </select>
  )
}

/** Il nome di una cartella come lo dice la colonna delle cartelle. */
function nomeCartella(c: CartellaTrovata): string {
  return c.tipo === 'cestino' ? 'Cestino del server' : c.nome
}

/**
 * Dove sta un risultato: la casella, se la ricerca ne guarda piu' d'una, le cartelle e «archiviata» per le
 * conversazioni archiviate, che la ricerca in tutte le cartelle comprende. Fuori dalla ricerca non c'e'.
 */
export function DoveTrovato({ casella, cartelle, archiviata = false }: {
  casella: string | null
  cartelle: (CartellaTrovata | null | undefined)[] | null | undefined
  archiviata?: boolean
}) {
  if (!casella && !cartelle) return null
  const nomi = [...new Set((cartelle ?? []).filter((c): c is CartellaTrovata => !!c).map(nomeCartella))]
  if (!casella && !nomi.length && !archiviata) return null
  const testo = [casella, nomi.join(', '), archiviata ? 'archiviata' : null].filter(Boolean).join(' · ')
  return (
    <span className="coda posta-dove" title={`In ${testo}`}>
      <IconaCartella />
      <span className="posta-dove-testo">{testo}</span>
    </span>
  )
}

/** Con questa scelta la ricerca guarda aperte e archiviate insieme, e la tendina non conta. */
export function suTutteLeCartelle(ambito: AmbitoRicerca | undefined, vistaUnica: boolean): boolean {
  return vistaUnica || ambito === 'casella' || ambito === 'caselle'
}

/** La nota sotto la barra mentre si cerca: dove e come. */
export function NotaRicerca({ ambito, nelCestino, vistaUnica, ordineDiverso }: {
  ambito: AmbitoRicerca
  nelCestino: boolean
  vistaUnica: boolean
  ordineDiverso: boolean
}) {
  const dove = nelCestino ? 'Si cerca solo nel cestino.'
    : vistaUnica || ambito === 'caselle'
      ? 'Si cerca in tutte le tue caselle, fra le conversazioni aperte e archiviate, tranne Indesiderata e Cestino del server.'
      : ambito === 'casella'
        ? 'Si cerca in tutte le cartelle della casella, fra le conversazioni aperte e archiviate, tranne Indesiderata e '
          + 'Cestino del server.'
        : 'Si cerca solo nella cartella aperta.'
  return (
    <p className="posta-nota" role="status">
      {dove} Parole intere e inizi di parola da 4 lettere («fatt» per «fattura») in tutto l’archivio; se così non
      si trova niente, anche le parti nel mezzo di una parola, negli ultimi 90 giorni.
      {ordineDiverso ? ' Con la ricerca l’elenco è in ordine di data.' : ''}
    </p>
  )
}
